// This file has tons of nested selector errors that should be fixed.
// eslint-disable-next-line local/no-block-disable-of-nested-eslint
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */

import { KeyboardEvent } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { Icon } from '@/libraries/wpl/foundations/src/icon';

import { CustomerStoryFilterObject, FilterCategories } from '../utils/types';

import { ComplexModalFilterCategorySection } from './complex-modal-filter-category-section';
import { CustomerStoryFilterModalComplexGrouping } from './customer-story-filter-modal-grouping';

interface CustomerStoryFilterModalComplexProps {
	numberOfCustomerStories: number;
	filterObject: CustomerStoryFilterObject;
	onlyShowMoreFiltersCategory?: boolean;
	updateFilterObject: (filterCategory: FilterCategories, subFilterName: string) => void;
	closeModal: () => void;
	clearMoreFilters?: () => void;
	resetFilters: () => void;
	clearFilters: () => void;
	runSearch: () => void;
}

export const CustomerStoryFilterModalComplex = ({
	numberOfCustomerStories,
	filterObject,
	onlyShowMoreFiltersCategory,
	updateFilterObject,
	closeModal,
	resetFilters,
	clearMoreFilters,
	clearFilters,
	runSearch,
}: CustomerStoryFilterModalComplexProps) => {
	const intl = useIntl();

	// Handle clicking outside the modal
	const handleClickingOutsideModal = (event: React.MouseEvent) => {
		if (event.target === event.currentTarget) {
			resetFilters();
			closeModal();
		}
	};

	// Handle "esc" key being pressed to close the modal
	const handleKeyDownOutsideModal = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			resetFilters();
			closeModal();
		}
	};

	// Handle click on the close button
	const handleClickingCloseModal = () => {
		resetFilters();
		closeModal();
	};

	const handleClearFilterClick = () => {
		if (onlyShowMoreFiltersCategory && clearMoreFilters) {
			clearMoreFilters();
		} else {
			clearFilters();
		}
	};

	let filterObjectKeys = Object.keys(filterObject);
	if (onlyShowMoreFiltersCategory) {
		filterObjectKeys = ['more filters'];
	}

	return (
		// Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details.
		// eslint-disable-next-line @atlassian/a11y/no-static-element-interactions,@atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			id="customer-story-modal-component-parent"
			css={customerStoryFilterModalBackgroundStyle}
			onKeyDown={handleKeyDownOutsideModal}
			onClick={handleClickingOutsideModal}
		>
			<div css={customerStoryFilterModalStyle}>
				<div className="filter-modal-header">
					<h3>Filters</h3>
					<button type="button" css={buttonBaselineStyle} onClick={handleClickingCloseModal}>
						<Icon name={'cross'} size={'small'} />
					</button>
				</div>
				<div className="filter-modal-body">
					{filterObjectKeys.map((filterCategory, index) => {
						const filterCategoryTyped = filterCategory as FilterCategories;

						if (filterCategoryTyped === 'more filters') {
							return (
								<CustomerStoryFilterModalComplexGrouping
									key={index}
									filterCategory="more filters"
									filterObject={filterObject}
									updateFilterObject={(subFilterName: string) =>
										updateFilterObject(filterCategoryTyped, subFilterName)
									}
								/>
							);
						}
						return (
							<ComplexModalFilterCategorySection
								key={index}
								filterCategory={filterCategoryTyped}
								filterObject={filterObject}
								updateFilterObject={updateFilterObject}
							/>
						);
					})}
				</div>
				<div className="filter-modal-footer">
					<button
						css={buttonBaselineStyle}
						type="button"
						className="filter-modal-clear-all"
						onClick={handleClearFilterClick}
					>
						{intl.formatMessage({
							id: 'CustomersLandingPage.clearAll',
							defaultMessage: 'Clear all',
						})}
					</button>
					<Adg4Button
						className="filter-apply-button"
						variant={'primary'}
						size={'small'}
						color={'blue'}
						onClick={runSearch}
					>
						<FormattedMessage
							id="CustomersLandingPage.showResults"
							defaultMessage="Show <numberOfCustomerStories></numberOfCustomerStories> results"
							values={{
								numberOfCustomerStories: () => numberOfCustomerStories,
							}}
						/>
					</Adg4Button>
				</div>
			</div>
		</div>
	);
};

const customerStoryFilterModalBackgroundStyle = css({
	position: 'fixed',
	// eslint-disable-next-line local/ensure-wac-token-usage
	backgroundColor: 'rgba(256, 256, 256, 0.7)',
	insetInlineStart: 0,
	insetInlineEnd: 0,
	insetBlockStart: 0,
	insetBlockEnd: 0,
	zIndex: 6, // navbar has zindex of 5
	width: '100%',
	height: '100%',
});

const customerStoryFilterModalStyle = css({
	[media.below.sm]: {
		width: '100%',
		maxWidth: '100%',
		marginBlockStart: '0',
		maxHeight: '100%',
		borderRadius: '0px',
	},
	[media.above.md]: {
		width: '630px',
		maxWidth: '630px',
		marginBlockStart: '64px',
	},
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	backgroundColor: '#FFFFFF',
	borderRadius: '20px',
	boxShadow: '0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F',
	padding: '32px',
	margin: '32px auto',
	width: '90%',
	maxHeight: '90%',
	'.filter-modal-header': {
		paddingBlockEnd: '32px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		// eslint-disable-next-line local/ensure-wac-token-usage
		borderBlockEnd: '1px dashed #DDDEE1',
		h3: {
			margin: 0,
			fontWeight: 700,
			fontSize: '2rem',
		},
		svg: {
			cursor: 'pointer',
			// eslint-disable-next-line local/ensure-wac-token-usage
			color: '#101214',
		},
	},
	'.filter-modal-body': {
		overflowY: 'scroll',
		scrollbarWidth: 'none',
		'-ms-overflow-style': 'none',
		'scrollbar-width': 'none',
		'-webkit-scrollbar': {
			display: 'none',
		},
		padding: '16px 0',
		h4: {
			margin: 0,
			fontWeight: 400,
			fontSize: '1.5rem',
			textTransform: 'capitalize',
		},
		'& > div': {
			paddingBlock: '24px',
			// eslint-disable-next-line local/ensure-wac-token-usage
			borderBlockEnd: '1px dashed #DDDEE1',
		},
		// We don't want the last border to show
		'& > div:last-child': {
			borderBlockEnd: 'none',
		},
	},

	'.filter-modal-grouping-container': {
		display: 'flex',
		flexWrap: 'nowrap',
		justifyContent: 'space-between',
		marginBlock: '0px',
		'.filter-modal-grouping-column': {
			[media.below.sm]: {
				'&.column-1': {
					flexBasis: '100%',
				},
				'&.column-2': {
					flexBasis: '0%',
				},
			},
			display: 'flex',
			flexDirection: 'column',
			flexWrap: 'wrap',
			justifyContent: 'flex-start',
			marginBlock: '0',
			paddingBlock: '0',
			'&.column-1': {
				flexBasis: '50%',
			},
			'&.column-2': {
				flexBasis: '50%',
			},

			'.filter-radio-button-container': {
				margin: '24px 0 0',
				cursor: 'pointer',
				display: 'flex',
				flexWrap: 'nowrap',
				alignItems: 'center',
				justifyContent: 'flex-start',
				':hover': {
					span: {
						// eslint-disable-next-line local/ensure-wac-token-usage
						border: '2px solid #1868DB',
					},
				},
				'&.hide-subfilter': {
					display: 'none',
				},
				span: {
					width: '20px',
					height: '20px',
					borderRadius: '3px',
					// eslint-disable-next-line local/ensure-wac-token-usage
					border: '2px solid #DDDEE1',
					flexBasis: '20px',
					marginInlineEnd: '8px',
					// eslint-disable-next-line local/ensure-wac-token-usage
					background: '#FFFFFF',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				},
				'span.checked': {
					// eslint-disable-next-line local/ensure-wac-token-usage
					background: '#1868DB',
					// eslint-disable-next-line local/ensure-wac-token-usage
					border: '2px solid #1868DB',
				},
				p: {
					margin: 0,
				},
			},
		},
	},
	'.filter-modal-body-more-filters': {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		'.filter-slider-button-container': {
			width: '100%',
			'.filter-slider-content': {
				padding: '24px 0',
				marginBlockEnd: '24px',
				display: 'flex',
				flexWrap: 'nowrap',
				justifyContent: 'space-between',
				alignItems: 'center',
				// eslint-disable-next-line local/ensure-wac-token-usage
				borderBlockEnd: '1px dashed #DDDEE1',
				p: {
					width: '75%',
					margin: 0,
				},
			},
			'.switch': {
				position: 'relative',
				display: 'inline-block',
				width: '40px',
				height: '20px',
				input: {
					opacity: 0,
					width: 0,
					height: 0,
					':checked + .slider': {
						// eslint-disable-next-line local/ensure-wac-token-usage
						background: '#1868DB',
					},
					':checked + .slider:before': {
						transform: 'translateX(20px)',
					},
				},
			},
			'.slider': {
				position: 'absolute',
				cursor: 'pointer',
				inset: 0,
				backgroundColor: '#DDDEE1',
				transition: '.4s',
				borderRadius: '34px',
				':before': {
					position: 'absolute',
					content: '""',
					height: '16px',
					width: '16px',
					insetInlineStart: '2px',
					insetBlockEnd: '2px',
					backgroundColor: 'white',
					transition: '.4s',
					borderRadius: '50%',
				},
			},
			'input:checked + .slider': {
				// eslint-disable-next-line local/ensure-wac-token-usage
				background: '#1868DB',
			},
			'input:checked + .slider:before': {
				transform: 'translateX(20px)',
			},
			'.slider.round': {
				borderRadius: '24px',
			},
			'.slider.round:before': {
				borderRadius: '50%',
			},
		},
	},
	'.filter-modal-footer': {
		padding: '32px 0 0',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderBlockStart: '1px dashed #DDDEE1',
		'.filter-apply-button': {
			':hover': {
				// eslint-disable-next-line local/ensure-wac-token-usage
				background: '#1868DB',
			},
		},
	},
});

const buttonBaselineStyle = css({
	border: 'none',
	background: 'transparent',
	padding: '0',
	fontSize: '1rem',
	fontFamily: 'Charlie Text',
	cursor: 'pointer',
	// eslint-disable-next-line local/ensure-wac-token-usage
	color: '#172B4D',
});
